/* eslint-disable no-empty-pattern */
/* eslint-disable no-var */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useConfirm } from "../../components/dialog/confirm";
import Header, { HeaderType } from "../../components/Header";
import { getAccessToken, useAuth } from "../../context/auth";
import usePosition from "../../hooks/usePosition";
import type { AccidentSupportRequest } from "../../services/model/accidentSupportRequest";
import { UpdateAccidentSupportService } from "../../services/api/accident-service";
import BrandLoader from "@/branding/BrandLoader";
import { BrandingContext } from "@/branding/BrandingProvider";
import { SetLocation, NavigateTo } from "@/helper/navigation";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

interface AccidentSupportFinalProps {
  style?: any;
  loading: boolean;
}

type NavItem = {
  name: string;
  id: string; // node/formId
  index: number; // step index
  formType: string;
  required?: boolean;
};

const STEP_DONE_PREFIX = "flowStepDone:"; // flowStepDone:<stepIndex>:<nodeId>
const EVIDENCE_PREFIX = "flowEvidence:"; // flowEvidence:<stepIndex>:<nodeId>

const AccidentSupportFinal: React.FC<AccidentSupportFinalProps> = (_props) => {
  const { latitude, longitude } = usePosition({ enableHighAccuracy: true });
  const navigate = useNavigate();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();
  const [supportNo, SetSupportNo] = useState(0);
  let [loading, setLoading] = useState(false);
  const [, setIsSubmit] = useState(false);
  const { tenant } = useParams<{ tenant: string }>();
  const branding = useContext(BrandingContext);
  const [sp] = useSearchParams();
  const step = sp.get("step") ?? "0";
  const node = sp.get("node") ?? "";
  const cameFromList = sp.get("from") === "list";

  const ORIGIN_STEP_KEY = "listOriginStep";
  const ORIGIN_NODE_KEY = "listOriginNode";

  const [steps, setSteps] = useState<NavItem[]>([]);

  const readNavigation = (): NavItem[] => {
    try {
      const raw = localStorage.getItem("navigation");
      const nav = raw ? (JSON.parse(raw) as any[]) : [];
      return (nav ?? [])
        .filter(Boolean)
        .map((x) => ({
          name: String(x.name ?? ""),
          id: String(x.id ?? ""),
          index: Number(x.index ?? 0),
          formType: String(x.formType ?? ""),
          required: Boolean(x.required),
        }))
        .sort((a, b) => a.index - b.index)
        .filter((x) => x.formType !== "Final Screen"); // exclude final screen from display
    } catch {
      return [];
    }
  };

  const stepDoneKeyFor = (s: number, nodeId: string) =>
    `${STEP_DONE_PREFIX}${s}:${nodeId}`;

  const evidenceKeyFor = (s: number, nodeId: string) =>
    `${EVIDENCE_PREFIX}${s}:${nodeId}`;

  const isStepDone = (s: number, nodeId: string) => {
    // "done" if:
    // - explicit completion flag exists (flowStepDone)
    // - OR evidence gate exists (flowEvidence) from required upload/draw logic
    // This makes the Final screen list useful even if some older flows only set evidence.
    return (
      localStorage.getItem(stepDoneKeyFor(s, nodeId)) === "1" ||
      localStorage.getItem(evidenceKeyFor(s, nodeId)) === "1"
    );
  };

  const FINAL_RETURN_FLAG_KEY = "returnToFinal";
  const FINAL_RETURN_URL_KEY = "finalReturnUrl";

  const goToStep = (s: number) => {
    const nav = steps.length ? steps : readNavigation();
    const rec = nav.find((x) => x.index === s);
    if (!rec) return;

    const base = tenant ? `/${tenant}` : "";

    // 1) Store a stable return target back to THIS Final screen instance
    // (keep the final step/node you are currently on)
    const finalStep = step ?? "0";
    const finalNode = node ?? "";

    const finalReturnUrl = `${base}/accidentSupportFinal?step=${finalStep}&node=${encodeURIComponent(finalNode)}`;

    sessionStorage.setItem(FINAL_RETURN_FLAG_KEY, "1");
    sessionStorage.setItem(FINAL_RETURN_URL_KEY, finalReturnUrl);

    // 2) Go to the chosen step WITHOUT disturbing the flow
    // SetLocation is fine because you are intentionally jumping
    SetLocation(s);

    // Build the step url, then add from=final
    const rawUrl = NavigateTo(); // ex: /uploads?step=2&node=...
    const absUrl = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    const urlWithFrom =
      absUrl + (absUrl.includes("?") ? "&" : "?") + "from=final";

    navigate(`${base}${urlWithFrom}`, { replace: false });
  };

  const clearFlowEvidence = () => {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(EVIDENCE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  };

  const clearStepDone = () => {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(STEP_DONE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  };

  const clearLocalImageCaches = () => {
    const prefixes = [
      "flowUploadCache:", // if you add this later
      "flowDrawCache:", // if you add this later
      "digialert_pwa_", // your offline-cached images
    ];

    const toRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;

      if (prefixes.some((p) => k.startsWith(p))) {
        toRemove.push(k);
        continue;
      }

      const v = localStorage.getItem(k);
      if (v && typeof v === "string" && v.startsWith("data:image/")) {
        toRemove.push(k);
      }
    }

    Array.from(new Set(toRemove)).forEach((k) => localStorage.removeItem(k));
  };

  function clearFormCache() {
    clearFlowEvidence();
    clearStepDone();
    localStorage.removeItem("navigation");
    localStorage.removeItem("accidentId");
    localStorage.removeItem("currentScreenIndex");
    localStorage.removeItem("formTypeId");
    localStorage.removeItem("VehicleId");
    localStorage.removeItem("documentCount");
    localStorage.removeItem("voiceCount");
    localStorage.removeItem("viewVehicleId");
    localStorage.removeItem("returnToList");
    localStorage.removeItem("returnStep");
    localStorage.removeItem("returnNode");
    localStorage.removeItem("linkedDocumentID");
    localStorage.removeItem("dynamicViewVehicleId");
  }

  const goToDashboard = () => {
    navigate(`/${tenant}/accidentsupportlist`, { replace: true });
  };



  const showDialog = (message: string, success: boolean) => {
    let title: string = "Form Submitted";
    if (success === false) title = "Form Submission Failed";

    confirm({
      catchOnCancel: false,
      title: title,
      description: message,
    }).then(() => {
      if (success) {
        clearLocalImageCaches();
        clearFormCache();
        goToDashboard();
      }
    });
  };

  useEffect(() => {
    sessionStorage.removeItem("returnToFinal");
    sessionStorage.removeItem("finalReturnUrl");
  }, []);

  useEffect(() => {
    if (cameFromList) {
      sessionStorage.setItem(ORIGIN_STEP_KEY, step);
      sessionStorage.setItem(ORIGIN_NODE_KEY, node);
    }
  }, [cameFromList, step, node]);

  useEffect(() => {
    setSteps(readNavigation());
  }, [step, node]);



  function getRandomNumberBetween(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  const submitButtonClick = (buttonType: string) => {
    if (buttonType === "submit") setIsSubmit(true);
    else setIsSubmit(false);
  };

  useEffect(() => {
    SetSupportNo(getRandomNumberBetween(1, 9999));
  }, []);

  const doneCount = useMemo(() => {
    if (!steps.length) return 0;
    return steps.reduce(
      (acc, s) => acc + (isStepDone(s.index, s.id) ? 1 : 0),
      0,
    );
  }, [steps]);

  return (
    <div>
      <Formik
        initialValues={{ supportNo: "" }}
        validationSchema={Yup.object().shape({})}
        onSubmit={async (fields) => {
          try {
            if (fields) {
              setLoading(true);

              let currentLatitude = latitude?.toString()!;
              let currentLongitude = longitude?.toString()!;

              var accidentId = JSON.parse(
                window.localStorage.getItem("accidentId") || "",
              );
              var formId = JSON.parse(
                window.localStorage.getItem("formId") || "",
              );

              var vehicleId: string = "00000000-0000-0000-0000-000000000000";
              var incidentType = JSON.parse(
                window.localStorage.getItem("incidentType") || "",
              );
              if (incidentType === "AccidentSupport") {
                vehicleId = JSON.parse(
                  window.localStorage.getItem("VehicleId") ||
                    "00000000-0000-0000-0000-000000000000",
                );
              }

              const accident: AccidentSupportRequest = {
                id: accidentId,
                latitude: currentLatitude,
                longitude: currentLongitude,
                notes: "",
                notes2: "",
                supportNo: supportNo.toString(),
                clientId: branding?.clientId,
                questionAnswers: null,
                vehicleId: vehicleId,
                formId: formId,
                status: "Pending",
              };

              const response = await UpdateAccidentSupportService(
                accidentId,
                accident,
                token,
              );

              if (response) {
                if (response.data) {
                  setLoading(false);
                  showDialog(response.message, response.resultCode);
                } else {
                  setLoading(false);
                  showDialog(
                    "There seems to be a problem, please contact your administrator or try again.",
                    false,
                  );
                }
              } else {
                setLoading(false);
                showDialog(
                  "There seems to be a problem with your internet connection. Please check your connection and try again.",
                  false,
                );
              }
            }
          } catch (err) {
            setLoading(false);
            showDialog(
              "An exception occurred, please contact your administrator.",
              false,
            );
            if (err instanceof Error) console.error(err.message);
          }
        }}
      >
        {(props) => {
          const {} = props;
          return (
            <>
              <Header type={HeaderType.AccidentSupportAdd} />
              <Container maxWidth="sm">
                <Card variant="outlined">
                  <CardContent>
                    <Form noValidate>
                      <Grid container spacing={1}>
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          direction="column"
                        >
                          <Typography variant="subtitle1">
                            <strong>Form Submission</strong>
                          </Typography>
                        </Grid>

                        <Grid size={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            <strong>Steps completed</strong>
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {doneCount} of {steps.length} steps marked as done.
                          </Typography>

                          <Card variant="outlined" sx={{ p: 1 }}>
                            <List dense disablePadding>
                              {steps.map((s) => {
                                const done = isStepDone(s.index, s.id);

                                return (
                                  <ListItemButton
                                    key={`${s.index}-${s.id}`}
                                    onClick={() => goToStep(s.index)}
                                    sx={{ borderRadius: 1 }}
                                  >
                                    {done ? (
                                      <CheckCircleIcon
                                        fontSize="small"
                                        color="success"
                                      />
                                    ) : (
                                      <RadioButtonUncheckedIcon
                                        fontSize="small"
                                        color="disabled"
                                      />
                                    )}

                                    <ListItemText
                                      sx={{ ml: 1 }}
                                      primary={s.name || `Step ${s.index + 1}`}
                                      secondary={`${s.formType}${s.required ? " • Required" : ""}`}
                                    />

                                    <Chip
                                      size="small"
                                      label={done ? "Done" : "Pending"}
                                      color={done ? "success" : "default"}
                                      variant={done ? "filled" : "outlined"}
                                    />
                                  </ListItemButton>
                                );
                              })}
                            </List>

                            {steps.length === 0 && (
                              <Box sx={{ p: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No steps found for this flow.
                                </Typography>
                              </Box>
                            )}
                          </Card>
                        </Grid>

                        <Grid size={12}>
                          <Typography variant="subtitle2">
                            <br />
                          </Typography>
                        </Grid>

                        <Grid size={12}>
                          <Button
                            type="submit"
                            value="Next"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            onClick={() => submitButtonClick("submit")}
                            disabled={loading}
                          >
                            <strong>Save & Submit</strong>
                          </Button>
                        </Grid>
                      </Grid>
                    </Form>
                  </CardContent>
                </Card>
              </Container>
            </>
          );
        }}
      </Formik>

      <BrandLoader open={loading} />
    </div>
  );
};

export default AccidentSupportFinal;
